import { ContractType } from "../../enums/ContractType";
import { VideoCV, WorkExperience } from "../job/JobApplication";
import { ExperienceSector } from "../job/ExperienceSector";
import { LicenseTypeEnum } from "../../enums/LicenseTypeEnum";
import { FinancialObjectiveEnum } from "src/utils/enums/FinancialObjectiveEnum";
import { ApplicationStatusEnum } from "src/utils/enums/ApplicationStatus";
import { AiInterviewDeclineReasons } from "../vetting/InterviewDetailsInterface";
import { OnboardingRequestStatusEnum, OnboardingTaskSubmissionStatusEnum } from "../onboarding/OnboardingTypes";
import { UtmObject } from "../utmdata/utmdata";
import { ScheduledInterviewDto } from "../interview-scheduling/InterviewSchedulingTypes";
import { ScreeningAnswersResponse } from "../screening-questions/ScreeningQuestionsInterfaces";
import { OnboardingTaskTypeEnum } from "src/utils/constants/onboarding-tasks";

export interface OnboardingTaskSubmission {
  externalId: string;
  status: OnboardingTaskSubmissionStatusEnum;
  metadata?: Record<string, any>;
  rejectionReason?: string;
  createdAt: number;
}


export interface OnboardingTask {
  externalId: string;
  name: string;
  type: OnboardingTaskTypeEnum;
  isQiwaRequirement: boolean;
  isOptional: boolean;
  isCompleted: boolean;
  additionalInstructions?: string;
  metadata?: Record<string, any>;
  submission?: OnboardingTaskSubmission;
}

export interface OnboardingRequest {
  externalId: string;
  status: OnboardingRequestStatusEnum;
  createdAt: number;
  expiresAt: number;
  totalTasks: number;
  completedTasks: number;
  tasks: OnboardingTask[];
  qiwaTasks: number;
  completedQiwaTasks: number;
  documentTasks: number;
  completedDocumentTasks: number;
}

export interface ApplicantListItem {
  id: string;
  age: number;
  externalId: string;
  applicationId: string;
  links: SocialIconItemInterface[];
  firstName: string;
  lastName: string;
  mobile: string;
  nationalId?: string;
  email: string;
  hasVideo?: boolean;
  profileImage: string;
  applicationLocation?: string;
  nationality: string;
  nationalityCategory: string;
  gender: string;
  isStudent: boolean;
  city: string;
  district: string;
  englishLevel: string;
  isNearby?: boolean;
  workStatus: string;
  experienceSector: ExperienceSector[];
  yearsOfExperience: string;
  hasHealthCard: boolean;
  previousJobs: string;
  workHours: string[];
  workDays: string[];
  transportation: string;
  pdfCvUrl: string;
  status: ApplicationStatusEnum;
  workSpecifics: {
    carrying: string;
    cleaning: string;
    customerAmounts: string;
    handlingCash: string;
    kids: string;
    moving: string;
    openCloseStore: string;
    standing: string;
    workOutsideJobDescription: string;
    genderSegregation: string;
  };
  acceptableWorkplaces: string[];
  requiredWorkSpecifics: {
    carrying: boolean;
    cleaning: boolean;
    handlingCash: boolean;
    kids: boolean;
    moving: boolean;
    openCloseStore: boolean;
    standing: boolean;
  };
  socialStatus: string;
  wantedWorkDuration: string;
  workplacePreferences: string;
  isUnlocked?: boolean;
  specialNeedsType?: boolean;
  minSalary?: number;
  maxSalary?: number;
  preferredSalary?: string;
  speaksEnglish?: boolean;
  willingToGosi?: boolean;
  wantedContractTypes?: ContractType[];
  appliedAt?: number;
  bio?: string;
  education: {
    fieldOfStudy?: string;
    id: string;
    institution?: string;
    level?: string;
    institutionLogo?: string;
  }[];
  employmentStatus: string;
  expectedSalary: number;
  hasSpecialNeed: boolean;
  healthCardExpiryDate: number;
  healthCardSubmissionStatus: string;
  isProfileImageVerified: boolean;
  rejectedWorkSpecifics: string[];
  acceptedWorkSpecifics: string[];
  videoCv: VideoCV[];
  videoCvEn: VideoCV[];
  workExperience: WorkExperience[];
  jobStartDelay: string;
  isNewApplicant?: boolean;
  note?: string;
  partnerNote?: string;
  workerNote?: string;
  applicantJoiningDate?: string;
  hasSaudiRelative?: boolean;
  hasPreviousExperience?: boolean;
  relevantExperiences?: { jobTitle: string, specialization: string, isHighlighted: boolean }[];
  hasRelevantExperience?: boolean;
  highlightedSkills?: { name: string, isHighlighted: boolean }[];
  skills?: { name: string, isHighlighted: boolean }[];
  isFar?: boolean;
  experienceTag?: { category: string, industry: string };
  lastMessage?: any;
  distance?: number;
  hasNoExperience?: boolean;
  language?: {
    id: string;
    language: string;
    proficiency: string;
    proficiencyValue: string;
  }[];
  recommendedAt?: number;
  training?: WorkerTrainingRecord[];
  license?: WorkerLicenseRecord[];
  lastActiveAt?: number;
  lastUpdateAt?: number;
  newUpdatesCount?: number;
  financialObjective?: FinancialObjectiveEnum;
  preferredJobTitles?: string[];
  canViewDetails?: boolean;
  profileImageUrl?: string;
  isOnline?: boolean;
  invitedAt?: number;
  isLookingForJob?: boolean;
  recentApplication?: {
    jobTitle: string;
    status: ApplicationStatusEnum;
  },
  exactExperience?: {
    jobTitle: string;
    isHighlighted: boolean;
    months?: number;
    years?: number;
    specialization?: string;
  }
  previouslyRejected?: boolean;

  invitedForVetting: boolean;
  vettingStatus: AiInterviewStatusEnum;
  interviewFinishedAt: number;
  aiDecision: AiInterviewDecisionEnum;
  aiSummary: string;
  isIrrelevant: boolean;
  aiInterviewReason: string;
  verifiedYrsExperienceInTitle: number;
  matchedContractType: boolean;
  matchedMonthlySalary: number;
  matchedJobTitle: string;
  matchedPoints: string[];
  unmatchedPoints: string[];
  interviewId: string;
  partnerSubmittedFeedback: boolean;
  declineReason: AiInterviewDeclineReasons;
  declineComment: string;
  isOffline: boolean;
  talentAcquisitionSync?: {
    isSynced: boolean;
    applicationNum?: string;
    lastSyncedAt?: number;
    lastStatus?: string;
  };
  stageName?: string;
  stageExternalId?: string;

  // new matching
  aiMatchingInitialDecision?: AiInterviewDecisionEnum;
  aiMatchingReason?: string;
  aiMatchingSummary?: string;
  aiMatchingScore?: number;
  aiMatchingMatchedPoints?: string[];
  aiMatchingUnmatchedPoints?: string[];
  showAiMatchingLabels?: boolean;

  // offline application
  source?: OfflineApplicationSourceEnum;
  utmMedium?: string;
  utmSource?: string;
  utmData?: UtmObject;
  referrer?: string;

  // whatsapp
  whatsappConversationId?: string;

  // screening answers
  screeningAnswers?: ScreeningAnswersResponse | null;

  // interview evaluations
  evaluationSummary?: {
    latestVerdict?: string | null;
    evaluationsCount: number;
    distinctEvaluators: number;
    myLockedEvaluationId?: string | null;
  } | null;

  // interview scheduling
  scheduledInterviews?: ScheduledInterviewDto[];
  numOfNotes?: number;
  lastNote?: {
    message: string;
    createdBy?: string | null;
  };

  // onboarding
  onboardingRequest?: OnboardingRequest;
}

export enum AiInterviewStatusEnum {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  DECLINED = 'DECLINED',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
  NO_INTERVIEW = 'NO_INTERVIEW', //Used for filter only
}

export enum SaudAudioInterviewPartnerEligibility {
  DISABLED = 'DISABLED',
  POPUP = 'POPUP',
  DEFAULT = 'DEFAULT',
}

export enum AiInterviewDecisionEnum {
  GOOD_FIT = 'GOOD_FIT',
  MAYBE = 'MAYBE',
  MISMATCH = 'MISMATCH',

  NO_INTERVIEW = 'NO_INTERVIEW', //Used for filter only

}

export interface WorkerTrainingRecord {
  id: string;
  institution: string;
  institutionLogo: string;
  program: string;
  programLogo: string;
  startDate: number;
  endDate: number;
}

export interface WorkerLicenseRecord {
  id: string;
  type: LicenseTypeEnum;
  typeValue: string;
  number?: string;
  expiryDate: number;
}


export interface SocialIconItemInterface {
  link: string;
  type: string;
  icon: string;
}

export enum OfflineApplicationSourceEnum {
  LINKEDIN = 'LINKEDIN',
  BAYT = 'BAYT',
  GOOGLE = 'GOOGLE',
  INDEED = 'INDEED',
  NAUKRI_GULF = 'NAUKRI_GULF',
  GLASSDOOR = 'GLASSDOOR',
  MONSTER_GULF = 'MONSTER_GULF',
  GULFTALENT = 'GULFTALENT',
  FACEBOOK = 'FACEBOOK',
  TWITTER = 'TWITTER',
  INSTAGRAM = 'INSTAGRAM',
  SNAPCHAT = 'SNAPCHAT',
  WHATSAPP = 'WHATSAPP',
  TELEGRAM = 'TELEGRAM',
  EMAIL = 'EMAIL',
  YOUTUBE = 'YOUTUBE',
  TIKTOK = 'TIKTOK',
  SAUDI_JOBS = 'SAUDI_JOBS',
  FORSAH = 'FORSAH',
  TANQEEB = 'TANQEEB',
  WEB = 'WEB'
}

export enum ApplicantsDragAndDropColumnIdEnum {
  APPLIED = 'applied',
  INTERVIEWED = 'interviewed',
  SHORTLISTED = 'shortlisted',
  FINALIST = 'finalist',
}

export interface DynamicKanbanColumn {
  id: string;
  title: string;
  order: number;
  status?: ApplicationStatusEnum;
  vettedBySaud?: boolean;
  filters?: any; // Additional filters for this column
}

export interface DynamicApplicationStatus {
  id: string;
  value: string; // The actual status value (e.g., "PENDING", "SHORT_LISTED")
  label: string; // Display label (e.g., "Applied", "Shortlisted")
  enum: ApplicationStatusEnum;
  order: number;
  isRejection?: boolean; // Whether this is a rejection status
}

export interface ApplicantsDragAndDropColumnInterface {
  id: ApplicantsDragAndDropColumnIdEnum;
  title: string;
  applicants: ApplicantListItem[];
}

export interface KanbanColumn {
  id: string; // Changed from ApplicantsDragAndDropColumnIdEnum to string for dynamic support
  title: string;
  applicants: ApplicantListItem[];
  page: number;
  status?: ApplicationStatusEnum;
  vettedBySaud?: boolean;
  count?: number;
}

export const isApplicantSyncedToTalentAcquisition = (applicant?: ApplicantListItem | null): boolean => {
  return !!applicant?.talentAcquisitionSync?.isSynced;
};

export const splitApplicantsForTalentAcquisitionSync = (applicants: ApplicantListItem[]) => {
  const unsynced = (applicants || []).filter(applicant => !isApplicantSyncedToTalentAcquisition(applicant));
  return {
    jobApplicationIds: unsynced.filter(applicant => !applicant.isOffline).map(applicant => applicant.externalId),
    offlineJobApplicationIds: unsynced.filter(applicant => applicant.isOffline).map(applicant => applicant.externalId),
    unsyncedCount: unsynced.length,
    skippedCount: (applicants || []).length - unsynced.length,
  };
};
